import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, ExternalLink, BookOpen, Headphones, Video, ListVideo, Library as LibraryIcon, FileText, Sparkles, Globe, Star } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SmartImage from '../components/SmartImage';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toResourceEmbed, isSoundcloud, isDirectAudio, isDirectVideo } from '../lib/embed';
import api from '../lib/api';

export const LIBRARY_CATEGORIES = [
  { key: 'library', label: 'Library', icon: LibraryIcon },
  { key: 'books', label: 'Books', icon: BookOpen },
  { key: 'audio', label: 'Audio', icon: Headphones },
  { key: 'video', label: 'Videos', icon: Video },
  { key: 'playlists', label: 'Playlists', icon: ListVideo },
  { key: 'blog', label: 'Blog Articles', icon: FileText },
  { key: 'wisdom', label: 'Wisdom by Topic', icon: Sparkles },
  { key: 'resources', label: 'Other Resources', icon: Star },
  { key: 'websites', label: 'Other Websites', icon: Globe },
];

const EMBED_CATS = ['audio', 'video', 'playlists'];

const ResourceItem = ({ item }) => {
  const isMediaCat = EMBED_CATS.includes(item.category);

  // Native player for direct file links (e.g. a .mp3 or .mp4 URL) so it PLAYS, not downloads.
  if (isMediaCat && isDirectAudio(item.url)) {
    return (
      <div className="rounded-3xl bg-white/70 border border-rose/40 p-4" data-testid={`library-item-${item.id}`}>
        <div className="font-serif-display text-lg text-burgundy">{item.title}</div>
        {item.author && <div className="text-xs text-mahogany/60">{item.author}</div>}
        {item.description && <p className="mt-1 text-sm text-mahogany/75">{item.description}</p>}
        <audio controls preload="none" className="w-full mt-3" data-testid={`library-audio-${item.id}`}>
          <source src={item.url} />
          Your browser does not support audio playback.
        </audio>
      </div>
    );
  }
  if (isMediaCat && isDirectVideo(item.url)) {
    return (
      <div className="rounded-3xl overflow-hidden bg-white/70 border border-rose/40" data-testid={`library-item-${item.id}`}>
        <video controls preload="metadata" className="w-full aspect-video bg-black" data-testid={`library-video-${item.id}`}>
          <source src={item.url} />
          Your browser does not support video playback.
        </video>
        <div className="p-4">
          <div className="font-serif-display text-lg text-burgundy">{item.title}</div>
          {item.author && <div className="text-xs text-mahogany/60">{item.author}</div>}
          {item.description && <p className="mt-1 text-sm text-mahogany/75">{item.description}</p>}
        </div>
      </div>
    );
  }

  const embed = isMediaCat ? toResourceEmbed(item.url) : null;
  if (embed) {
    const audio = item.category === 'audio' || isSoundcloud(item.url);
    return (
      <div className="rounded-3xl overflow-hidden bg-white/70 border border-rose/40" data-testid={`library-item-${item.id}`}>
        <div className={audio ? '' : 'aspect-video'}>
          <iframe
            title={item.title}
            src={embed}
            className="w-full border-0"
            style={audio ? { height: 166 } : { height: '100%', width: '100%' }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="p-4">
          <div className="font-serif-display text-lg text-burgundy">{item.title}</div>
          {item.author && <div className="text-xs text-mahogany/60">{item.author}</div>}
          {item.description && <p className="mt-1 text-sm text-mahogany/75">{item.description}</p>}
          <a href={item.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm text-rose-italic hover:underline">
            Open source <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    );
  }
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col rounded-3xl overflow-hidden bg-white/70 border border-rose/40 hover:border-rose hover:shadow-lg transition-all"
      data-testid={`library-item-${item.id}`}
    >
      <div className="relative aspect-[16/10] bg-rose-soft/40">
        {item.thumbnail_url
          ? <SmartImage src={item.thumbnail_url} alt={item.title} className="w-full h-full" />
          : <div className="w-full h-full flex items-center justify-center text-mahogany/30 font-serif-display text-xl">Osho</div>}
        {item.is_featured && <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest bg-mahogany text-cream px-2 py-1 rounded-full">Featured</span>}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="font-serif-display text-lg text-burgundy group-hover:text-mahogany transition-colors">{item.title}</div>
        {item.author && <div className="text-xs text-mahogany/60">{item.author}</div>}
        {item.description && <p className="mt-1 text-sm text-mahogany/75 line-clamp-3">{item.description}</p>}
        <span className="mt-auto pt-3 inline-flex items-center gap-1 text-sm text-rose-italic">Open <ExternalLink className="w-3.5 h-3.5" /></span>
      </div>
    </a>
  );
};

const OshoLibrary = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/library/links').then((r) => setLinks(r.data || [])).catch(() => setLinks([])).finally(() => setLoading(false));
  }, []);

  const byCat = useMemo(() => {
    const map = {};
    for (const l of links) (map[l.category] = map[l.category] || []).push(l);
    return map;
  }, [links]);

  const activeCats = LIBRARY_CATEGORIES.filter((c) => (byCat[c.key] || []).length > 0);
  const [tab, setTab] = useState(null);
  useEffect(() => { if (!tab && activeCats.length) setTab(activeCats[0].key); }, [activeCats, tab]);

  return (
    <div className="App">
      <Header />
      <main className="bg-cream min-h-screen">
        <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16">
          <div className="text-center">
            <div className="eyebrow mb-3">Osho Library</div>
            <h1 className="font-serif-display text-4xl md:text-5xl text-burgundy">
              Explore Osho’s <span className="font-italic-display text-rose-italic">Universe</span>
            </h1>
            <p className="mt-3 text-mahogany/80 max-w-2xl mx-auto">
              Books, audio, videos, playlists and curated resources — a living library of Osho’s work.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center mt-16"><Loader2 className="w-8 h-8 animate-spin text-mahogany" /></div>
          ) : activeCats.length === 0 ? (
            <div className="text-center mt-16 text-mahogany/60" data-testid="library-empty">
              The library is being curated. Please check back soon. 🌸
            </div>
          ) : (
            <Tabs value={tab || activeCats[0].key} onValueChange={setTab} className="mt-10">
              <TabsList className="flex flex-wrap h-auto gap-2 bg-transparent p-0" data-testid="library-tabs">
                {activeCats.map((c) => (
                  <TabsTrigger key={c.key} value={c.key} data-testid={`library-tab-${c.key}`}
                    className="data-[state=active]:bg-mahogany data-[state=active]:text-cream rounded-full px-4 py-2 border border-rose/50 bg-white/60 text-burgundy">
                    <c.icon className="w-4 h-4 mr-1.5" /> {c.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              {activeCats.map((c) => (
                <TabsContent key={c.key} value={c.key} className="mt-8">
                  <div className={`grid gap-6 ${c.key === 'audio' ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
                    {(byCat[c.key] || []).map((item) => <ResourceItem key={item.id} item={item} />)}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default OshoLibrary;
